import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import MessageAPI from '../../../../axios/message';
import { MessageIcon } from '../../../../components/shared-components/message-icon';
import { PanelConfirm } from '../panel-ui';

const datesAreOnSameDay = (first, second) =>
    first.getFullYear() === second.getFullYear() &&
    first.getMonth() === second.getMonth() &&
    first.getDate() === second.getDate();

const formatDate = (value) => {
    if (!value) return "";
    const d = new Date(value);
    return datesAreOnSameDay(d, new Date())
        ? d.toLocaleTimeString('sr-RS', { hour: '2-digit', minute: '2-digit' })
        : d.toLocaleDateString('sr-RS');
};

// replies carry the message they answer under this line
const QUOTE_SEPARATOR = '\n\n-------------------------\n\n';

const splitQuote = (body = '') => {
    const at = body.indexOf(QUOTE_SEPARATOR);
    return at < 0 ? [body, ''] : [body.slice(0, at), body.slice(at + QUOTE_SEPARATOR.length)];
};

export const UserMessage = ({ data, type, onRemoved }) => {
    const { t } = useTranslation(["message", "common"]);
    const received = type === "received_messages";
    const person = received ? data.sender : data.recipient;
    const [text, quote] = splitQuote(data.body);
    const menuRef = useRef(null);

    const [open, setOpen] = useState(false);
    const [unread, setUnread] = useState(received && !data.is_read);
    const [replying, setReplying] = useState(false);
    const [reply, setReply] = useState("");
    const [sending, setSending] = useState(false);
    // { message, label, onConfirm }
    const [confirm, setConfirm] = useState(null);
    // { ok, text }
    const [status, setStatus] = useState(null);
    const [quoteOpen, setQuoteOpen] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);

    useEffect(() => {
        const close = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
        };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, []);

    const toggle = () => {
        setOpen(!open);
        if (!open && unread) {
            MessageAPI.markReadMessage(data.id).then(() => setUnread(false)).catch(e => console.error(e));
        }
    };

    const startReply = () => {
        if (!open) toggle();
        setReplying(true);
        setStatus(null);
    };

    const sendReply = () => {
        if (!reply.trim() || sending) return;
        // the original message is quoted under the reply
        const original = `${new Date(data.date_created).toLocaleString('sr-RS')} : ${data.sender.first_name} ${data.sender.last_name}\n${data.body}`;
        setSending(true);
        MessageAPI.sendMessage({
            subject: "Re: " + data.subject,
            body: `${reply}${QUOTE_SEPARATOR}${original}`,
            reply_to: data.id,
            listing: data.listing,
        }).then(() => {
            setReply("");
            setReplying(false);
            setStatus({ ok: true, text: t("reply_sent") });
        }).catch(() => {
            setStatus({ ok: false, text: t("new_message_error") });
        }).finally(() => setSending(false));
    };

    const block = () => {
        MessageAPI.blockUser(data.sender.id)
            .then(() => setStatus({ ok: true, text: t("block_success") }))
            .catch(() => setStatus({ ok: false, text: t("block_error") }));
    };

    const remove = () => {
        MessageAPI.deleteMessage(data.id).then(onRemoved)
            .catch(() => setStatus({ ok: false, text: t("delete_error") }));
    };

    const menu = [
        received && { label: t('block'), onClick: () => setConfirm({ message: t("block_confirm_message"), label: t('block'), onConfirm: block }) },
        { label: t('delete'), danger: true, onClick: () => setConfirm({ message: t("delete_confirm_message"), label: t('delete'), onConfirm: remove }) },
    ].filter(Boolean);

    return (
        <div className={`hs-panel-message ${unread ? 'unread' : ''} ${open ? 'open' : ''}`}>
            <button className='hs-panel-message-main' onClick={toggle} aria-expanded={open}>
                <span className='hs-panel-message-avatar'>{`${person.first_name?.[0] || ''}${person.last_name?.[0] || ''}`.toUpperCase()}</span>
                <span className='hs-panel-message-text'>
                    <span className='hs-panel-message-top'>
                        <span className='hs-panel-message-name'>{!received && t('to')}{person.first_name} {person.last_name}</span>
                        <span className='hs-panel-message-date'>{formatDate(data.date_created)}</span>
                        {unread && <span className='hs-panel-message-dot'></span>}
                    </span>
                    <span className='hs-panel-message-subject'>{data.subject}</span>
                    {!open && <span className='hs-panel-message-preview'>{text}</span>}
                </span>
            </button>
            {(open || status) && (
                <div className='hs-panel-message-body'>
                    {open && <p>{text}</p>}
                    {open && quote && <>
                        <button className='hs-panel-text-btn' onClick={() => setQuoteOpen(!quoteOpen)}>{t(quoteOpen ? 'hide_quote' : 'show_quote')}</button>
                        {quoteOpen && <p className='hs-panel-message-quote'>{quote}</p>}
                    </>}
                    {open && data.listing && <Link to={`/oglas/${data.listing}`} className='hs-panel-badge blue'>{t('listing_link', { id: data.listing })}</Link>}
                    {open && (replying
                        ? <div className='hs-panel-reply'>
                            <textarea autoFocus rows={4} placeholder={t('reply_placeholder')} value={reply} onChange={(e) => setReply(e.target.value)} />
                            <div className='hs-panel-reply-actions'>
                                <button className='hs-panel-outline' onClick={() => setReplying(false)}>{t('cancel', { ns: 'common' })}</button>
                                <button className='hs-panel-primary' disabled={!reply.trim() || sending} onClick={sendReply}>{t('send')}</button>
                            </div>
                        </div>
                        : <div className='hs-panel-message-actions'>
                            {received && <button className='hs-panel-soft' onClick={startReply}><MessageIcon size={16} />{t('reply')}</button>}
                            <div className='hs-panel-more' ref={menuRef}>
                                <button className='hs-panel-outline icon' aria-label={t('more')} onClick={() => setMenuOpen(!menuOpen)}>⋯</button>
                                {menuOpen && (
                                    <div className='hs-panel-pop hs-panel-more-pop'>
                                        {menu.map(item => (
                                            <button key={item.label} className={`hs-panel-pop-item ${item.danger ? 'danger' : ''}`}
                                                onClick={() => { setMenuOpen(false); item.onClick(); }}>
                                                <span className='hs-panel-pop-label'>{item.label}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>)}
                    {status && <span className={`hs-panel-status ${status.ok ? 'ok' : 'err'}`}>{status.text}</span>}
                </div>
            )}
            {confirm && <PanelConfirm danger title={t("block_confirm_title")} message={confirm.message} confirmLabel={confirm.label}
                onClose={() => setConfirm(null)} onConfirm={confirm.onConfirm} />}
        </div>
    );
};
